/**
 * Renders sanitised course content, then enhances it in the DOM:
 *   * Mermaid diagrams drawn in `securityLevel: 'strict'` mode;
 *   * a copy button on every code block;
 *   * the table of contents lifted out for the reader's sidebar.
 *
 * Mermaid is imported lazily so that the ~500 kB library is only downloaded on
 * pages that actually contain a diagram.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

import { renderMarkdown, type TocEntry } from '@/components/content/markdown';
import type { Mermaid } from 'mermaid';
import { cn } from '@/lib/cn';

interface Props {
  source: string;
  className?: string;
  onToc?: (toc: TocEntry[]) => void;
}

let mermaidPromise: Promise<Mermaid> | null = null;

async function loadMermaid() {
  mermaidPromise ??= import('mermaid').then((module) => {
    module.default.initialize({
      startOnLoad: false,
      // 'strict' forbids HTML labels and inline scripts inside diagram source.
      securityLevel: 'strict',
      theme: 'base',
      fontFamily: 'Inter, system-ui, sans-serif',
      themeVariables: {
        primaryColor: '#16213f',
        primaryTextColor: '#e2e8f0',
        primaryBorderColor: '#3b82f6',
        lineColor: '#60a5fa',
        secondaryColor: '#1b1838',
        tertiaryColor: '#0f172a',
        background: '#0f172a',
        mainBkg: '#16213f',
        nodeBorder: '#3b82f6',
        clusterBkg: '#0b1220',
        clusterBorder: '#26334f',
        textColor: '#e2e8f0',
        fontSize: '13px',
      },
    });
    return module.default;
  });
  return mermaidPromise;
}

export function MarkdownView({ source, className, onToc }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { html, toc, diagrams } = useMemo(() => renderMarkdown(source), [source]);

  useEffect(() => {
    onToc?.(toc);
  }, [toc, onToc]);

  // Mermaid rendering.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ids = Object.keys(diagrams);
    if (ids.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const mermaid = await loadMermaid();
        for (const id of ids) {
          if (cancelled) return;
          const host = container.querySelector(`#${CSS.escape(id)}`);
          const definition = diagrams[id];
          if (!host || !definition) continue;
          try {
            const { svg } = await mermaid.render(`${id}-svg`, definition);
            if (!cancelled) host.innerHTML = svg;
          } catch {
            // An invalid diagram shows its source rather than breaking the page.
            host.textContent = definition;
            host.classList.add('font-mono', 'text-xs', 'whitespace-pre-wrap');
          }
        }
      } catch (error) {
        // Mermaid failing to load must not prevent reading the chapter, but the
        // failure must be visible: a silently blank diagram is worse than a
        // readable fallback.
        console.error('[KH AI Academy] rendu Mermaid indisponible', error);
        for (const id of ids) {
          const host = container.querySelector(`#${CSS.escape(id)}`);
          const definition = diagrams[id];
          if (host && definition && !host.innerHTML) {
            host.textContent = definition;
            host.classList.add('font-mono', 'text-xs', 'whitespace-pre-wrap');
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [diagrams, html]);

  // Copy buttons on code blocks.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const cleanups: (() => void)[] = [];

    container.querySelectorAll('pre').forEach((pre) => {
      if (pre.dataset.khaEnhanced === 'true') return;
      pre.dataset.khaEnhanced = 'true';
      pre.classList.add('relative', 'group');

      const button = document.createElement('button');
      button.type = 'button';
      button.className =
        'kha-no-print absolute right-2 top-2 rounded-md border border-border bg-surface/90 ' +
        'px-2 py-1 text-2xs font-semibold text-muted opacity-0 transition-opacity ' +
        'hover:text-fg focus:opacity-100 group-hover:opacity-100';
      button.textContent = 'Copier';
      button.setAttribute('aria-label', 'Copier le bloc de code');

      const onClick = async () => {
        const code = pre.querySelector('code')?.textContent ?? '';
        try {
          await navigator.clipboard.writeText(code);
          button.textContent = 'Copié';
        } catch {
          // Clipboard permission denied: tell the user instead of failing silently.
          button.textContent = 'Indisponible';
        }
        window.setTimeout(() => {
          button.textContent = 'Copier';
        }, 1600);
      };
      button.addEventListener('click', onClick);
      pre.appendChild(button);
      cleanups.push(() => {
        button.removeEventListener('click', onClick);
        button.remove();
        delete pre.dataset.khaEnhanced;
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, [html]);

  return (
    <div
      ref={containerRef}
      className={cn('kha-prose', className)}
      // Safe: `html` is the output of DOMPurify with a strict allow-list.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** Small standalone copy button used outside Markdown (lab command lists). */
export function CopyButton({ value, label = 'Copier' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
        } catch {
          setCopied(false);
        }
      }}
      className="kha-no-print inline-flex shrink-0 items-center gap-1 rounded-md border border-border
                 bg-surface px-2 py-1 text-2xs font-semibold text-muted transition-colors
                 hover:border-tech hover:text-tech"
      aria-label={`${label} : ${value}`}
    >
      {copied ? <Check className="h-3 w-3 text-ok" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copié' : label}
    </button>
  );
}

export function MermaidDiagram({ definition }: { definition: string }) {
  // A standalone diagram reuses the exact same sanitised pipeline.
  return <MarkdownView source={`\`\`\`mermaid\n${definition}\n\`\`\``} />;
}
